/* Jedinstveno mesto na kome se računaju AETR prekršaji.
   Koriste ga i tab KAZNE AETR i DIJAGRAM AKTIVNOSTI — da se tekstovi poruka i
   način dolaženja do kazne NIKAD ne raziđu. Nova pravila se dodaju SAMO ovde. */
/* Pravila iz AETR čl. 6–8 računa aetrMotor.js na periodima rad/odmor.
   Preostali moduli (noćni rad/vožnja, loše upravljanje, istek kartice) su
   domaći propisi vezani za kalendarski dan i ostaju kakvi jesu. */
import { izracunajPoPravilima }        from './aetrMotor';
import { processRecordsWork }         from './faultSumWork';
import { processRecordsNightDriving } from './faultNightDriving';
import { processUnknown }             from './faultUnknown';
import { processCardExpiryDate }      from './faultCardExpiry';
import { SVI_PROPISI, propisPravila, pravilaZa, kategorijaZa } from './propisi';
import { lokalniDan, pocetakLokalnogDana, pomeriDan } from './vreme';

/* Redosled je i redosled prikaza u tabeli KAZNE AETR.
   `propis` se NE piše ovde rukom — izvodi se iz registra (propisi.js), da postoji
   jedno jedino mesto na kom stoji koje pravilo pripada kom propisu. */
export const AETR_GRUPE = [
    { key: 'unknown',      label: 'Loše upravljanje tahografom' },
    { key: 'dayRest',      label: 'Dnevni odmor' },
    { key: 'sumDriving',   label: 'Prekoračenje vožnje' },
    { key: 'twoWeek',      label: 'Dvonedeljno prekoračenje' },
    { key: 'sumWork',      label: 'Ukupan rad' },
    { key: 'nightDriving', label: 'Noćna vožnja' },
    { key: 'less45',       label: 'Pauza 45 minuta' },
    { key: 'weekRest',     label: 'Vikend odmor' },
    { key: 'workTime',     label: 'Dnevno radno vreme' },
    { key: 'cardExpiry',   label: 'Istek kartica' },
].map(g => ({ ...g, propis: propisPravila(g.key) }));

export const AETR_LABELE = AETR_GRUPE.reduce((a, g) => { a[g.key] = g.label; return a; }, {});

/* Vozači se izvode iz samih zapisa (isti izvor kao u obe komponente). */
export function vozaciIzZapisa(records) {
    const m = new Map();
    (records || []).forEach(r => {
        if (!m.has(r.DriverId)) m.set(r.DriverId, { Id: r.DriverId, FullName: r.HolderName });
    });
    return Array.from(m.values());
}

/**
 * Računa sve AETR prekršaje za date zapise.
 *
 * @param {Array}  records      aktivnosti sa /tacho/faults
 * @param {Array}  cards        kartice sa /tacho/faults (za istek kartice)
 * @param {string} companyName  naziv firme (ide u kolonu Kompanija)
 * @param {Date}   dateTo       kraj perioda (nedeljni odmor gleda do ovog datuma)
 * @param {string} prikazOd     'YYYY-MM-DD' — datum od kog korisnik traži prikaz.
 *                              Zapisi stižu i ranije (kontekst za pravila koja
 *                              gledaju nedeljama unazad), pa se prekršaji koji su
 *                              se završili pre ovog datuma ne prikazuju.
 * @returns {Array<{key, label, data}>} grupe u fiksnom redosledu (prazne izostaju)
 */
export function izracunajPrekrsaje({ records, cards, companyName, dateTo, prikazOd, prikazDo, propisi, idDrzava }) {
    const co = { Name: companyName || '' };
    const zapisi = Array.isArray(records) ? records : [];
    const driversDB = vozaciIzZapisa(zapisi);

    const cardRecords = (cards || []).map(c => ({ DriverId: c.Id, ExpiryDate: c.ExpiryDate }));
    const cardDrivers = (cards || []).map(c => ({ Id: c.Id, FullName: c.HolderName }));

    /* Izabrani propisi određuju i koja se pravila puštaju i koji limiti važe.
       Prazno → svi, da se izborom ničim ne sakrije ono što se do sada videlo. */
    const izabrani = Array.isArray(propisi) && propisi.length ? propisi : SVI_PROPISI;
    const dozvoljena = pravilaZa(izabrani, idDrzava);

    const aetr = izracunajPoPravilima({ records: zapisi, companyName: co.Name, dateTo, propisi: izabrani, idDrzava });

    /* Pravila van izabranih propisa se NE računaju — ne samo da se ne prikazuju.
       Tako izbor „samo AETR 2010" ne troši vreme na domaće module i ne može da
       propusti nešto kroz filter prikaza. */
    const ako = (kljuc, f) => (dozvoljena.includes(kljuc) ? f() : []);

    const rezultati = {
        unknown:      ako('unknown',      () => (zapisi.length ? processUnknown(zapisi, co, driversDB, false) : [])),
        dayRest:      aetr.dayRest,
        sumDriving:   aetr.sumDriving,
        twoWeek:      aetr.twoWeek,
        sumWork:      ako('sumWork',      () => (zapisi.length ? processRecordsWork(zapisi, co, driversDB) : [])),
        nightDriving: ako('nightDriving', () => (zapisi.length ? processRecordsNightDriving(zapisi, co, driversDB) : [])),
        less45:       aetr.less45,
        weekRest:     aetr.weekRest,
        workTime:     aetr.workTime,
        /* Prekršaj je SAMO istekla kartica (vožnja sa nevažećom karticom).
           Kartica koja tek ističe nije prekršaj — vidi pregledIstekaKartica(). */
        cardExpiry:   ako('cardExpiry',   () => samoIstekle(processCardExpiryDate(cardRecords, co, cardDrivers))),
    };

    /* Granice traženog perioda su LOKALNE ponoći, ne UTC — inače bi se prekršaj
       nastao u 01:00 po Beogradu (23:00 UTC prethodnog dana) našao van perioda. */
    /* Zaposleni vozač prekršaja (task 108) — po njemu se prekršaj spaja sa ispisom
       ili kaznom sa telefona. Moduli redove pišu samo sa imenom vozača (HolderName),
       a ime je jedinstveno jer nosi i broj kartice. */
    const zaposleniPoImenu = new Map();
    zapisi.forEach(r => { if (r.EmployeeId != null) zaposleniPoImenu.set(r.HolderName, r.EmployeeId); });
    (cards || []).forEach(c => { if (c.EmployeeId != null) zaposleniPoImenu.set(c.HolderName, c.EmployeeId); });

    const odMs = prikazOd ? pocetakLokalnogDana(prikazOd) : null;
    const doMs = prikazDo ? pocetakLokalnogDana(prikazDo) + 24 * 60 * 60 * 1000 - 1 : null;

    return AETR_GRUPE
        .filter(g => dozvoljena.includes(g.key))
        .map(g => ({
            ...g,
            data: (Array.isArray(rezultati[g.key]) ? rezultati[g.key] : [])
                .filter(x => uPeriodu(x, odMs, doMs))
                .map(x => dopuniPropis(x, g))
                .map(x => (x && x.EmployeeId == null && zaposleniPoImenu.has(x.Vozač)
                    ? { ...x, EmployeeId: zaposleniPoImenu.get(x.Vozač) } : x)),
        }))
        .filter(g => g.data.length > 0);
}

/* Motor (aetrMotor.js) sam upisuje Propis/Kategorija/Clan jer zna Podtip. Stariji
   moduli (sumWork, unknown, cardExpiry) ne postavljaju Podtip, pa bi im ta polja
   ostala prazna i u dokumentu se ne bi znalo po kom su propisu prekršaj.

   Zato se propis OBAVEZNO pečatira sa nivoa grupe — grupa svoj propis takođe
   dobija iz registra, pa i dalje postoji samo jedno mesto istine. Kategorija se
   dopunjuje samo ako se Podtip prepozna; ne izmišlja se. */
function dopuniPropis(x, grupa) {
    if (!x || (x.Propis && x.Kategorija)) return x;
    const kat = kategorijaZa(x.Podtip);
    return {
        ...x,
        Propis: x.Propis || (kat ? kat.propis : grupa.propis) || null,
        Kategorija: x.Kategorija || (kat ? kat.naziv : null),
        KategorijaRedni: x.KategorijaRedni ?? (kat ? kat.redni : null),
    };
}

/* Prekršaj pripada danu u kome se završava i prikazuje se samo ako taj dan pada
   u traženi period. Zapisi izvan perioda služe pravilima kao kontekst. */
function uPeriodu(x, odMs, doMs) {
    if (!x) return false;
    const dan = danPrekrsaja(x);
    if (!dan) return true;
    const ms = Date.parse(dan + 'T12:00:00.000Z');
    if (odMs != null && ms < odMs) return false;
    if (doMs != null && ms > doMs) return false;
    return true;
}

/* processCardExpiryDate vraća red za SVAKU karticu ("Kartica ističe - ..."),
   a prekršaj je samo ona koja je već istekla ("KARTICA JE ISTEKLA - ..."). */
function samoIstekle(redovi) {
    return (redovi || []).filter(r => String(r?.Poruka || '').startsWith('KARTICA JE ISTEKLA'));
}

/** Pun pregled isteka kartica (i istekle i buduće) — za filter "Istek kartica". */
export function pregledIstekaKartica({ cards, companyName }) {
    const co = { Name: companyName || '' };
    const cardRecords = (cards || []).map(c => ({ DriverId: c.Id, ExpiryDate: c.ExpiryDate }));
    const cardDrivers = (cards || []).map(c => ({ Id: c.Id, FullName: c.HolderName }));
    return processCardExpiryDate(cardRecords, co, cardDrivers);
}

/* Prekršaji nose datum samo u tekstu poruke (formatSRBTime → DD.MM.YYYY).
   Prvi datum u poruci je dan na koji se prekršaj odnosi. */
export function danIzPoruke(poruka) {
    const m = /(\d{2})\.(\d{2})\.(\d{4})/.exec(String(poruka || ''));
    return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
}

/** Ravna lista svih prekršaja sa oznakom grupe, danom i intervalom — za dijagram. */
export function ravnaListaPrekrsaja(args) {
    const out = [];
    izracunajPrekrsaje(args).forEach(g => g.data.forEach(x => {
        if (!x || !x.Poruka) return;
        /* Interval iz motora je precizan; stari moduli ga nemaju, pa se dan
           izvlači iz teksta poruke kao ranije. */
        const od = x.Pocetak ? new Date(x.Pocetak).getTime() : null;
        const doo = x.Kraj ? new Date(x.Kraj).getTime() : null;
        const dan = od != null ? lokalniDan(od) : danIzPoruke(x.Poruka);
        const { opis, kazna } = razdvojPoruku(x.Poruka);
        out.push({
            ...x,
            _tip: g.key, _grupa: g.label, _day: dan, _od: od, _do: doo,
            /* Moduli koji još nemaju strukturisana polja dobijaju bar vrstu i vreme,
               da red u tabeli ne ostane prazan. */
            Tip: x.Tip || g.label,
            /* `dan` je lokalan, a `Vreme` je stvarni UTC (pomera se tek u prikazu) —
               fallback je zato UTC trenutak lokalne ponoći, ne `dan + 'Z'`. */
            Vreme: x.Vreme || x.Kraj
                || (dan ? new Date(pocetakLokalnogDana(dan)).toISOString() : null),
            _opis: opis,
            _kazna: kazna,
        });
    }));
    return out;
}

/** Poruka je "<opis> — Kazna: <iznosi>" — razdvaja se za prikaz u tabeli. */
export function razdvojPoruku(poruka) {
    const s = String(poruka || '');
    const i = s.indexOf(' — Kazna:');
    if (i < 0) return { opis: s, kazna: '' };
    return { opis: s.substring(0, i), kazna: s.substring(i + 3) };
}

/** Dan kome se prekršaj PRIPISUJE — onaj u kome se period/aktivnost završava.
 *  Prekršaj koji traje preko ponoći tako ostaje jedan, na danu završetka.
 *  Radi i sa sirovim prekršajem (Pocetak/Kraj) i sa ravnom listom (_od/_do). */
export function danPrekrsaja(f) {
    if (!f) return null;
    const kraj = f._do != null ? f._do : (f.Kraj ? new Date(f.Kraj).getTime() : null);
    const pocetak = f._od != null ? f._od : (f.Pocetak ? new Date(f.Pocetak).getTime() : null);
    if (kraj != null) {
        /* Kraj tačno u ponoć pripada danu koji se ZAVRŠAVA, ne narednom —
           odmor do 24:00 ne sme da "pobegne" u sledeći dan. */
        const ms = (kraj % 86400000 === 0 && pocetak != null && pocetak < kraj) ? kraj - 1 : kraj;
        return lokalniDan(ms);
    }
    if (pocetak != null) return lokalniDan(pocetak);
    return f._day || danIzPoruke(f.Poruka);
}

/** Dani (YYYY-MM-DD) koje prekršaj dodiruje — interval zna da pređe ponoć.
 *  Koristi se SAMO za crveni pojas na dijagramu, ne za brojanje.
 *
 *  Dani su LOKALNI, jer su takvi i dani na dijagramu. Ranije je ovde stajalo
 *  `Date.UTC(...)` i hod od +24h, pa je pojas padao na UTC dan — prekršaj koji
 *  po zidnom satu počinje 23.06 u 01:25 crtao se na 22.06 u 23:25. */
export function daniPrekrsaja(f) {
    if (f._od == null) return f._day ? [f._day] : [];
    const dani = [];
    const kraj = f._do != null ? f._do : f._od;
    let dan = lokalniDan(f._od);
    while (dan) {
        /* Prvi dan ulazi uvek; svaki sledeći samo ako počinje pre kraja
           prekršaja — prekršaj koji se završava tačno u ponoć ne curi dalje. */
        if (dani.length && pocetakLokalnogDana(dan) >= kraj) break;
        dani.push(dan);
        if (dani.length > 400) break;      // zaštita od loših podataka
        /* sledeći dan po KALENDARU, ne po +24h: lokalni dan na prelazu na
           letnje/zimsko vreme ima 23 ili 25 sati */
        dan = pomeriDan(dan, 1);
    }
    return dani;
}
